import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";
import d from "dom-to-image-more";
import React, { useEffect, useRef } from "react";

import { COLORS } from "utils/colors";

var origin;
var target;
var svg;

/**
 * SkyPlot component renders a polar sky plot using D3 that visualizes
 * current and target azimuth and elevation positions as circles.
 * @memberof module:components/graphs
 * @param {Object} props
 * @param {number} props.width - Width of the SVG container
 * @param {number} props.height - Height of the SVG container
 * @param {number} props.azCurrent - Current azimuth angle in degrees
 * @param {number} props.elCurrent - Current elevation angle in degrees
 * @param {number} props.azTarget - Target azimuth angle in degrees
 * @param {number} props.elTarget - Target elevation angle in degrees
 * @param {boolean} props.disabled - Whether the plot is disabled (opacity changes)
 *
 * @returns {JSX.Element} Rendered SVG sky plot
 */
const PolarPlot = ({
  width,
  height,
  azCurrent,
  elCurrent,
  dataset,
  defaultOptiopns,
}) => {
  const ref = useRef();

  var margin = 70;
  var radius = Math.min(width, height) / 2 - margin;

  /**
   * Effect to clear and redraw the SVG plot once on component mount.
   */
  useEffect(() => {
    if (d3.select(ref.current)) d3.select(ref.current).selectAll("*").remove();

    draw(true);
  }, [dataset]);

  const drawTooltip = () => {
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "black")
      .style("color", "white")
      .style("padding", "6px 10px")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0);

    const hoverLine = svg
      .append("line")
      .attr("stroke", "yellow")
      .attr("stroke-width", 1.5)
      .style("opacity", 0);
  };

  /**
   * Draws the sky plot with polar coordinate circles and direction labels.
   *
   * @param {boolean} animationEnabled - Whether animations should be enabled (currently unused)
   */
  const draw = (animationEnabled) => {
    // Create dummy data

    svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

    svg
      .append("text")
      .attr("y", -height / 2 + 15)
      .attr("font-size", "15px")
      .attr("text-anchor", "middle")
      .attr("stroke", "white")
      .attr("letter-spacing", "0.054em")
      .text(defaultOptiopns.title);

    const safeData = Array.isArray(dataset) ? dataset : [];

    const t0 = safeData.length > 0 ? safeData[0].timestamp : 0;

    const normalizedData = safeData.map((d) => ({
      bearing: d.value,
      timestamp: d.timestamp,
      time: d.timestamp - t0,
    }));


    const maxTime = d3.max(normalizedData, (d) => d.time);

    var r = d3.scaleLinear().domain([0, maxTime]).range([30, radius]);

    var gr = svg
      .append("g")
      .attr("class", "r axis")
      .selectAll("g")
      .data(r.ticks(5))
      .enter()
      .append("g");

    gr.append("circle").attr("r", r);

    svg
      .append("g")
      .attr("class", "r boundary")
      .append("circle")
      .attr("r", radius);

    var ga = svg
      .append("g")
      .attr("class", "a axis")
      .selectAll("g")
      .data(d3.range(0, 360, 45))
      .enter()
      .append("g")
      .attr("fill", "white")
      .attr("transform", function (d) {
        return "rotate(" + (d - 90) + ")";
      });

    ga.append("line").attr("x2", radius);

    const directionMap = {
      0: "N",
      90: "E",
      180: "S",
      270: "W",
    };

    ga.append("text")
      .attr("x", radius + 12)
      .attr("text-anchor", "middle")
      .attr("transform", (d) => {
        if (d === 180) return `rotate(-90 ${radius + 12},0) translate(0,10)`;

        return `rotate(90 ${radius + 12},0)`;
      })
      .text((d) => (directionMap[d] ? `${directionMap[d]} (${d}°)` : ""));

    svg.append("g").attr("transform", "rotate(-90)");

    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "white")
      .style("padding", "6px 10px")
      .style("border", "1px solid #ccc")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("font-family", "'Open Sans', sans-serif")
      .style("font-size", "12px")
      .style("z-index", "13")
      .style("opacity", 0);

    const focusLine = svg
      .append("line")
      .attr("stroke", "#999")
      .attr("stroke-dasharray", "3,3")
      .style("opacity", 0);

    const focus = svg
      .append("circle")
      .attr("r", 5)
      .attr("fill", "red")
      .style("opacity", 0);

    const circles = svg
      .selectAll(".point")
      .data(normalizedData)
      .enter()
      .append("circle") // only one append per circle
      .attr("class", "point")
      .attr("fill", "red")
      .attr("r", 3)
      .attr(
        "cx",
        (d) => r(d.time) * Math.cos(((d.bearing - 90) * Math.PI) / 180),
      )
      .attr(
        "cy",
        (d) => r(d.time) * Math.sin(((d.bearing - 90) * Math.PI) / 180),
      )
      .on("mouseover", function (event, d) {
        focus.style("opacity", 1);
        const x = r(d.time) * Math.cos(((d.bearing - 90) * Math.PI) / 180);
        const y = r(d.time) * Math.sin(((d.bearing - 90) * Math.PI) / 180);

        // Show radial line
        focusLine
          .attr("x1", 0)
          .attr("y1", 0)
          .attr("x2", x)
          .attr("y2", y)
          .style("opacity", 1);
        focus.attr("cx", x).attr("cy", y);

        // Show tooltip
        tooltip.style("opacity", 1).html(`
        <b>Date:</b> ${new Date(d.timestamp).toISOString()}
          <br><b>Bearing (degs):</b> ${d.bearing}°<br/>
        
      `);
      })
      .on("mousemove", function (event) {
        tooltip
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 20 + "px");
      })
      .on("mouseout", function () {
        focusLine.style("opacity", 0);
        focus.style("opacity", 0);
        tooltip.style("opacity", 0);
      });
  };
  return (
    <>
      <div
        className="bar-graph p-2"
        id="container"
        style={{ justifyContent: "center" }}
      >
        <svg
          ref={ref}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        ></svg>
      </div>
    </>
  );
};

export default PolarPlot;
